import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionTab } from 'primereact/accordion';
import mixpanel from 'mixpanel-browser';
import { ReactComponent as GreenCheckMarkIcon } from '../../../assets/icons/svg/colored-icons/ic-colored-green-checkmark.svg';
import Heading from '../../../generic-components/Heading';
import { PROFILE_STYLES, PROFILE_TEXTS } from '../constants';
import Button from '../../../generic-components/Button';
import Icon from '../../../generic-components/Icon';
import CloseIcon from '../../../assets/svg/x-icon-v2.svg';
import { ReactComponent as BankIcon } from '../../../assets/icons/svg/colored-icons/ic-colored-bank.svg';
import { ReactComponent as DomesticIcon } from '../../../assets/icons/svg/colored-icons/ic-colored-domesticwire.svg';
import { ReactComponent as IntWire } from '../../../assets/icons/svg/colored-icons/ic-colored-int-wire2.svg';
import {
  AccordionHeaderType,
  AccordionTabHeaderTemplate,
  LiliAccordion,
} from '../../../DesignSystem/LiliAccordion/LiliAccordion';
import { TextWithCopyItem } from './TextWithCopyItem';
import { FEATURE_FLAG } from '../../../constants/general';
import { validateFeatureFlag } from '../../../services/featureFlagService';

interface IProps {
  onClose: () => void;
  achNumber?: string;
  achRoutingNumber?: string;
  wireRoutingNumber?: string;
  fullName: string;
  swiftCode?: string;
}

export const AccountDetailsModal = ({
  onClose,
  achNumber,
  achRoutingNumber,
  wireRoutingNumber,
  fullName,
  swiftCode,
}: IProps) => {
  const { t } = useTranslation();
  const [activeCopyType, setActiveCopyType] = useState<boolean>(false);
  const timeOutRefId = useRef<any>(null);
  const hideInternationalWire = !validateFeatureFlag(FEATURE_FLAG.INTERNATIONAL_WIRE_IN_TRANSFER);
  const copyDetails = () => {
    const body = `Beneficiary: ${fullName}\nAccount Number: ${achNumber}\nRouting Number for ACH: ${achRoutingNumber}\n Routing number for Wire Transfer: ${wireRoutingNumber} ${swiftCode && !hideInternationalWire ? `\n Swift number for International Wire Transfer: ${swiftCode}` : ' '}`;
    window.navigator.clipboard.writeText(body);
    if (timeOutRefId.current) {
      clearTimeout(timeOutRefId.current);
    }
    setActiveCopyType(true); // Set to true when details are copied
    mixpanel.track('Act on Account Details Bottom Sheet', {
      Action: 'Share',
    });
  };

  useEffect(() => {
    if (activeCopyType) {
      timeOutRefId.current = setTimeout(() => {
        setActiveCopyType(false); // Reset to false after timeout
        clearTimeout(timeOutRefId.current);
      }, 1500);
    }
  }, [activeCopyType]);

  useEffect(() => {
    mixpanel.track('View Account Details Bottom Sheet');
  }, []);
  const copied = t('DIRECT_DEPOSIT_PAGE_COPIED');

  const Header = (title: string) =>
    useMemo(
      () => (
        <div className='flex'>
          <Heading title='For ' className='  font-metric text-[1rem] text-bw08 pr-1' />
          <Heading type='HTML' title={title} className='font-metricMedium text-[1rem] text-bw08' />
        </div>
      ),
      [t],
    );
  const routingNumberTitle = t('WIRE_ROUT_NUM');
  const AccountNumber = t('INT_WIRE_ACCOUNT_NUM');
  const accountDetails = useMemo(
    () => [
      {
        title: t('DETAILS_MODAL.ACH_TITLE'),
        value: achRoutingNumber,
        routingText: routingNumberTitle,
        type: 'ACH',
        icon: BankIcon,
        mixPanelKey: 'Copy Routing Number for ACH',
        id: 'accordionTab_ach',
      },
      {
        title: t('DETAILS_MODAL.DOMESTIC_TRANSFER'),
        value: wireRoutingNumber,
        routingText: routingNumberTitle,
        type: 'WIRE',
        icon: DomesticIcon,
        mixPanelKey: 'Copy Routing Number for Wire',
        id: 'accordionTab_domesticWire',
      },
      swiftCode
        ? {
          title: t('DETAILS_MODAL.INTERNATIONAL_TRANSFER'),
          value: swiftCode,
          routingText: t('INT_WIRE_ROUT_NUM'),
          type: 'INT WIRE',
          icon: IntWire,
          mixPanelKey: 'Copy International Wire Transfer',
          hidden: hideInternationalWire,
          id: 'accordionTab_internationalWire',
        }
        : null,
    ],
    [achNumber, achRoutingNumber, wireRoutingNumber, swiftCode],
  );

  return (
    <div className='w-[458px] h-[553px] relative rounded-[12px] bg-bw01 flex flex-col'>
      <div className='flex items-center justify-between px-[28px] py-[20px] border-b border-bw03 mb-[11px]'>
        <Heading
          title={t(PROFILE_TEXTS.ACCOUNT_DETAILS_HEADER)}
          className={PROFILE_STYLES.ACCOUNT_DETAILS_SECTION_HEADER_MODAL}
        />
        <Button
          onClick={onClose}
          childElement={<Icon className='w-[20px]' src={CloseIcon} />}
          className='rounded-[50%] hover:bg-bw02 px-[4px] py-[2px]'
          id='elButtonCloseAccountDetails'
        />
      </div>

      <div className='flex items-center justify-end px-[24px] h-[24px] mb-[11px]'>
        {!activeCopyType ? (
          <Button
            onClick={() => copyDetails()}
            childElement={
              <Heading
                title={t('ACCOUNT_DETAILS_COPY')}
                className='font-metricRMedium text-[14px] text-action03 hover:underline'
              />
            }
            className=''
            id='elButtonCopyDetails'
          />
        ) : (
          <div className='flex items-center gap-[6px]  justify-center'>
            <GreenCheckMarkIcon className='w-[24px] h-[24px]' />
            <p className='text-badgeGreen02 text-sm font-metricR'>{copied}</p>
          </div>
        )}
      </div>

      <div className='flex flex-col rounded-md  bg-bw00 border-bw03 border-b-0 mb-[24px] mx-[28px]'>
        <LiliAccordion type={AccordionHeaderType.OUTLINED}>
          {accountDetails.map(
            (item) =>
              item &&
              !item.hidden && (
                <AccordionTab
                  key={item.title}
                  headerTemplate={AccordionTabHeaderTemplate({
                    Header: () => Header(item.title),
                    Icon: item.icon,
                    id: item.id,
                  })}
                >
                  <div className='flex flex-col items-start justify-center'>
                    <div className='border-t border-t-bw04  pr-2 pl-4 w-full py-[11px] '>
                      {achNumber && (
                        <TextWithCopyItem
                          title={AccountNumber}
                          text={achNumber}
                          mixPanelKey='copy Account number'
                          mixpanelTrack='Act on Account Details Bottom Sheet'
                          id='ACCOUNT_NUMBER'
                        />
                      )}
                    </div>
                    <div className='border-t border-t-bw04 pr-2 pl-4 py-[11px] w-full'>
                      {item.value && (
                        <TextWithCopyItem
                          title={item.routingText}
                          text={item.value}
                          mixPanelKey={item.mixPanelKey}
                          mixpanelTrack='Act on Account Details Bottom Sheet'
                          id='ROUTING'
                        />
                      )}
                    </div>
                  </div>
                </AccordionTab>
              ),
          )}
        </LiliAccordion>
      </div>
    </div>
  );
};
